/**
 * Project: spaces
 * File: Login
 * Created by Pennycodes on 2/4/2022.
 * Copyright spaces
 */
import React, { FormEvent, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { toAbsoluteUrl } from "../../../layout/helpers";
import queryString from "query-string";
import * as localService from "../../../services/localService";
// import apiService from "../../../services/apiService";
import { apiV2Service } from "../../../services/apiV2Service";
import { usePostAuthFlow } from "../../../shared/auth";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import { showApiMessage } from "../../../utils/messageHandler";
// import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import logger from "../../../utils/logger";
import { useAnalytics } from "../../../components/Analytics";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Button } from "../../../shadcn/ui/button";
import SEO from "../../../components/SEO/SEO";

// Type assertions for Shadcn components
const TypedInput = Input as any;
const TypedLabel = Label as any;
const TypedButton = Button as any;

// Types (keeping only used ones)

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEmptyEmail, setIsEmptyEmail] = useState(false);
  const [isEmptyPassword, setIsEmptyPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const search = queryString.parse(location.search);
  const params = search && search.payload ? search.payload : "";

  const redirectPath = (() => {
    const redirectValue = search?.redirect;
    if (!redirectValue) return null;
    if (Array.isArray(redirectValue)) {
      const first = redirectValue[0];
      return typeof first === "string" && first.startsWith("/") ? first : null;
    }
    if (typeof redirectValue === "string") {
      return redirectValue.startsWith("/") ? redirectValue : null;
    }
    return null;
  })();

  // Get analytics tracking functions
  const { trackLoginAttempt, trackLoginFailure } = useAnalytics();

  // Get shared post-auth flow
  const { handlePostAuthFlow } = usePostAuthFlow();

  function hasText(text: string) {
    return text != null && text !== "";
  }

  useEffect(() => {
    if (hasText(email)) {
      setIsEmptyEmail(false);
      if (email.includes(" ")) {
        setUsernameError("Username cannot contain spaces");
      } else {
        setUsernameError("");
      }
    } else {
      setUsernameError("");
    }
    if (hasText(password)) {
      setIsEmptyPassword(false);
    }
  }, [email, password]);

  // No-op effect: we intentionally avoid auto-revealing the password field
  // Autofilled password will be read when user explicitly reveals (Enter/Continue)
  useEffect(() => {}, []);

  // Sync password values between hidden and visible fields
  useEffect(() => {
    const hiddenPasswordInput = document.getElementById("password-hidden") as HTMLInputElement;
    if (hiddenPasswordInput && password !== hiddenPasswordInput.value) {
      hiddenPasswordInput.value = password;
    }
  }, [password]);

  const areFieldsOkay = () => {
    // Check for any spaces in the username
    if (email.includes(" ")) {
      setUsernameError("Username cannot contain spaces");
      return false;
    }
    setUsernameError("");

    const requiredFields = [email, password];
    // is any of the fields empty
    const anyEmpty = requiredFields.some(field => !field);
    if (anyEmpty) {
      if (!email) {
        setIsEmptyEmail(true);
      }
      if (!password) {
        setIsEmptyPassword(true);
      }
      return false;
    } else {
      return true;
    }
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!areFieldsOkay()) return;

    setLoading(true);
    trackLoginAttempt("email");

    try {
      const res = await apiV2Service.loginV2(email, password);
      if (!res || !res.user || !res.access_token) {
        throw new Error("Invalid response format");
      }

      // Use shared post-auth flow
      logger.log("About to call handlePostAuthFlow with:", {
        user: res.user,
        hasAccessToken: !!res.access_token,
        hasRefreshToken: !!res.refresh_token,
      });

      await handlePostAuthFlow(
        {
          user: res.user,
          access_token: res.access_token,
          refresh_token: res.refresh_token,
        },
        redirectPath
          ? {
              customNavigation: () => {
                history.push(redirectPath);
              },
            }
          : undefined
      );
    } catch (err) {
      logger.error("Login Error:", err);
      onLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  const onLoginError = (err: any) => {
    const { response } = err;

    // Set default error message
    let errorMessage = "A problem was encountered, please try again";

    if (response) {
      // Handle specific status codes
      if (response.status === 401) {
        const data = response.data;

        // Check for PASSWORD_CHANGE_REQUIRED error (by error code)
        if (data?.error === "PASSWORD_CHANGE_REQUIRED") {
          // Track login failure with specific reason
          trackLoginFailure("email", "Password change required", "password_change_required");

          // Redirect to password rotation page with username and password pre-filled
          // This allows user to directly set a new password without email flow
          history.push(
            `/password-rotation?username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
          );
          return;
        }

        // Check if response contains a redirectUrl for password reset
        if (data?.redirectUrl && data?.redirectUrl?.includes("resetpassword")) {
          // Intercept and redirect to our password rotation page instead
          history.push(
            `/password-rotation?username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
          );
          return;
        }

        errorMessage = "Invalid email or password";
      } else {
        // Try to get message from different possible locations in response
        const data = response.data;
        if (data) {
          if (data.message && data.message !== "Unauthorized") {
            // Use top-level message if available and not generic
            errorMessage = data.message;
          } else if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
            // Fall back to first error message if available
            const { message } = data.errors[0];
            errorMessage = message || errorMessage;
          }
        }
      }
    }

    // Track login failure
    trackLoginFailure("email", errorMessage, response?.status === 401 ? "invalid_credentials" : "server_error");

    logger.log("Login Error:", {
      status: response?.status,
      message: errorMessage,
      fullError: err,
    });

    // Use standardized message handler with the actual error response
    showApiMessage(err, "login");
  };

  // const googleLogin = async () => {
  //   // Track Google login attempt
  //   trackLoginAttempt("google");

  //   try {
  //     const auth = getAuth();
  //     const provider = new GoogleAuthProvider();
  //     provider.setCustomParameters({
  //       prompt: "select_account",
  //     });

  //     const result = await signInWithPopup(auth, provider);
  //     const credential = GoogleAuthProvider.credentialFromResult(result);
  //     const accessToken = credential?.accessToken;

  //     if (!accessToken) {
  //       throw new Error("No access token received from Google");
  //     }

  //     const response = await apiService.googleAuth({
  //       accessToken,
  //       companyId: 1,
  //       provider: "GOOGLE",
  //     });

  //     const user = response.data.data[0];

  //     // Track successful Google login
  //     trackLogin("google", user);

  //     // The redirect and user logic is now handled inline, so this call is no longer needed.
  //   } catch (error: any) {
  //     // Track Google login failure
  //     trackLoginFailure("google", error.message || "Google authentication failed", "google_auth_error");

  //     logger.error("Google login error:", error);
  //     onLoginError(error);
  //   }
  // };

  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  let payload = null;

  let decodedString = "";
  if (typeof params === "string") {
    decodedString = atob(params);
  }
  if (isValidJSON(decodedString)) {
    payload = JSON.parse(decodedString);
    // localStorage.setItem('payload',payload)
    localService.saveUser(payload);
  }

  const togglePasswordVisibility = (e: any) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const handleContinueClick = async () => {
    if (!showPasswordField) {
      handleUsernameStep();
    } else {
      if (!areFieldsOkay()) return;
      setLoading(true);
      trackLoginAttempt("email");
      try {
        const res = await apiV2Service.loginV2(email, password);
        if (!res || !res.user || !res.access_token) {
          throw new Error("Invalid response format");
        }
        await handlePostAuthFlow(
          {
            user: res.user,
            access_token: res.access_token,
            refresh_token: res.refresh_token,
          },
          redirectPath
            ? {
                customNavigation: () => {
                  history.push(redirectPath);
                },
              }
            : undefined
        );
      } catch (err) {
        logger.error("Login Error:", err);
        onLoginError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUsernameStep = () => {
    if (!email) {
      setIsEmptyEmail(true);
      return;
    }

    if (email.includes(" ")) {
      setUsernameError("Username cannot contain spaces");
      return;
    }

    setUsernameError("");
    setIsEmptyEmail(false);
    setShowPasswordField(true);
    // If browser already autofilled hidden password, sync it now
    const hiddenPasswordInput = document.getElementById("password-hidden") as HTMLInputElement;
    if (hiddenPasswordInput && hiddenPasswordInput.value) {
      setPassword(hiddenPasswordInput.value);
    }
    // Focus password field after reveal
    setTimeout(() => {
      const passwordInput = document.getElementById("password-visible") as HTMLInputElement;
      if (passwordInput) passwordInput.focus();
    }, 100);
  };

  return (
    <>
      <SEO
        title="Sign In to Spacia | Login to Your Account"
        description="Sign in to your Spacia account to access workspaces, accommodations, and experiences across Africa. Book spaces, manage reservations, and more."
        keywords={["spacia login", "sign in spacia", "spacia account", "login workspace", "spacia sign in"]}
        url={typeof window !== "undefined" ? `${window.location.origin}/signin` : ""}
        noindex={true}
      />
      <div className="w-full h-screen bg-white overflow-hidden">
        <div className="flex h-full md:flex-row">
          {/* Left Column - Login Form */}
          <div className="flex flex-col justify-center px-6 w-full md:w-1/2 lg:w-1/2 xl:w-1/2 md:px-8 lg:px-12">
            <div className="max-w-xs mx-auto w-full">
              {/* Logo - Above Get Started */}
              <div className="mb-6">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => history.push("/home")}>
                  <img
                    alt="logo"
                    src={toAbsoluteUrl("/assets/img/spacia/web/spacia_logo.png")}
                    className="w-auto h-20"
                  />
                  {/* <div className="flex flex-col">
                  <span className="text-gray-900 font-bold text-lg">SPACIA</span>
                  <span className="text-gray-500 text-xs">WORK STAY PLAY</span>
                </div> */}
                </div>
              </div>

              <div className="space-y-5">
                <div className="py-3">
                  <h1 className="text-2xl font-bold text-gray-900">Get Started with SPACIA</h1>

                  <p className="text-gray-600">
                    Don't have an account?{" "}
                    <Link to="/package-signup" className="text-blue-600 hover:text-blue-700 transition-colors">
                      Sign up →
                    </Link>
                  </p>
                </div>

                {/* Login Form - Always render for password manager detection */}
                <form onSubmit={handleLogin} className="space-y-3">
                  <div>
                    <TypedLabel htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Enter your username
                    </TypedLabel>
                    <div className="relative">
                      <TypedInput
                        type="text"
                        id="email"
                        name="username"
                        autoComplete="username"
                        placeholder="spaciahost"
                        value={email}
                        onMouseDown={() => setHasUserInteracted(true)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          // Do not mark as user interaction on change to avoid browser autofill triggering it
                          setEmail(e.target.value);
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (!hasUserInteracted) setHasUserInteracted(true);
                          if (e.key === "Enter" && !showPasswordField) {
                            e.preventDefault();
                            handleUsernameStep();
                          }
                        }}
                        className={`pl-3 pr-3 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 ${isEmptyEmail || usernameError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                      />
                      {isEmptyEmail ? (
                        <p className="mt-1 text-sm text-red-500">Please enter your username</p>
                      ) : usernameError ? (
                        <p className="mt-1 text-sm text-red-500">{usernameError}</p>
                      ) : null}
                    </div>
                  </div>

                  {/* Hidden password field - always in DOM for password manager */}
                  <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
                    <input
                      type="password"
                      id="password-hidden"
                      name="password"
                      autoComplete="current-password"
                      tabIndex={-1}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // Sync value but do NOT reveal here; reveal only on Enter/Continue
                        setPassword(e.target.value);
                      }}
                    />
                  </div>

                  <div
                    className="transition-all duration-500 ease-in-out overflow-hidden"
                    style={{ maxHeight: showPasswordField ? "200px" : "0", opacity: showPasswordField ? 1 : 0 }}
                  >
                    <div>
                      <TypedLabel htmlFor="password-visible" className="block text-sm font-medium text-gray-700 mb-1">
                        Enter your password
                      </TypedLabel>
                      <div className="relative">
                        <div className="relative">
                          <TypedInput
                            type={showPassword ? "text" : "password"}
                            id="password-visible"
                            autoComplete="current-password"
                            placeholder="••••••••••••"
                            value={password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (!areFieldsOkay()) return;
                                setLoading(true);
                                trackLoginAttempt("email");
                                try {
                                  const res = await apiV2Service.loginV2(email, password);
                                  if (!res || !res.user || !res.access_token) {
                                    throw new Error("Invalid response format");
                                  }
                                  await handlePostAuthFlow(
                                    {
                                      user: res.user,
                                      access_token: res.access_token,
                                      refresh_token: res.refresh_token,
                                    },
                                    redirectPath
                                      ? {
                                          customNavigation: () => {
                                            history.push(redirectPath);
                                          },
                                        }
                                      : undefined
                                  );
                                } catch (err) {
                                  logger.error("Login Error:", err);
                                  onLoginError(err);
                                } finally {
                                  setLoading(false);
                                }
                              }
                            }}
                            className={`pl-3 pr-12 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 ${isEmptyPassword ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="absolute right-3 top-0 bottom-0 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                          </button>
                        </div>
                        {isEmptyPassword && <p className="mt-1 text-sm text-red-500">Please enter your password</p>}
                      </div>
                    </div>
                  </div>

                  {/* Forgot Password Link - Always visible, positioned after password field */}
                  <div className="flex justify-end">
                    <Link
                      to="/resetpassword"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <TypedButton
                    type="button"
                    onClick={handleContinueClick}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2"
                    disabled={loading}
                  >
                    Continue
                    {loading && (
                      <svg
                        className="ml-2 w-5 h-5 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                  </TypedButton>
                </form>

                {/* <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">OR</span>
                </div>
              </div>

              <TypedButton
                type="button"
                variant="outline"
                onClick={googleLogin}
                className="w-full border-gray-300 text-gray-900 hover:bg-gray-50 py-2 bg-white"
              >
                <img src={toAbsoluteUrl("/img/google.png")} alt="Google Sign In" className="mr-2 w-4 h-4" />
                Continue with Google
              </TypedButton> */}

                {/* Terms */}
                <p className="text-sm text-gray-600">
                  By signing up, you agree to the{" "}
                  <a
                    href="/terms-of-use"
                    className="text-gray-700 hover:text-gray-900"
                    style={{ borderBottom: "1px solid #374151", paddingBottom: "1px" }}
                  >
                    terms of service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    className="text-gray-700 hover:text-gray-900"
                    style={{ borderBottom: "1px solid #374151", paddingBottom: "1px" }}
                  >
                    privacy policy
                  </a>
                  .
                </p>
              </div>

              {/* Support Link */}
              <div className="mt-6">
                <p className="text-sm text-gray-600">
                  Need help?{" "}
                  <a href="/support" className="text-blue-600 hover:text-blue-700">
                    Contact support
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Background Image */}
          <div className="hidden relative overflow-hidden md:block md:w-1/2 lg:w-1/2 xl:w-1/2 bg-[#0047AB]">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-no-repeat will-change-transform"
              style={{
                backgroundImage: `url(${toAbsoluteUrl("/img/loginbg.jpg")})`,
                backgroundPosition: "left top",
                backgroundSize: "cover",
                transform: "scale(1.5)",
                transformOrigin: "left top",
              }}
            />

            {/* Simplified gradient for better performance */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/90 via-blue-600/40 to-blue-400/20 will-change-transform"></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
